import {
  Icon,
  Loader,
  type PromptResolve,
} from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal, Select } from '@/components_deprecated'
import { Field, Form } from '@/modules/companies'
import React from 'react'
import { ExportFormat } from '@/modules/exports'
import { useExportFormatOptions } from '../exports/hooks'
import { LeaderGroupCombobox } from '@/components/combobox'
import { Checkbox } from '@/components'
import { SALARY_BASE_ENTRIES } from '../salary/queries'
import { useQuery } from '@apollo/client'

interface ExportSalaryBaseEntriesPromptProps {
  salaryBaseId: string
  initialLeaderGroups?: string[]
  initialOnlyNonExported?: boolean

  onSubmit: PromptResolve<{
    exportFormat: ExportFormat
    leaderGroups: string[]
    onlyNonExported: boolean
  } | null>
}

export const ExportSalaryBaseEntriesPrompt: React.FC<
  ExportSalaryBaseEntriesPromptProps
> = ({
  salaryBaseId,
  onSubmit,
  initialLeaderGroups = [],
  initialOnlyNonExported = false,
}) => {
  const translations = useTranslate({
    export: 'common.export',
    exportFormat: 'common.export-format',
    excel: 'common.excel',
    pdf: 'common.pdf',
    userFilter: 'common.user-filter',
    leaderGroups: 'companies.leader-groups',
    onlyNonExported: 'salary.only-non-exported',
    exportNLines: ['salary.export-n-lines', { n: 0 }],
  })

  const options = useExportFormatOptions()

  const {
    formValues: form,
    updateForm,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm({
    values: {
      exportFormat: ExportFormat.PDF,
      leaderGroups: initialLeaderGroups,
      onlyNonExported: initialOnlyNonExported,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'Export-equipments-form',
        transformValues: {
          exportFormat: value => value as ExportFormat,
        },
      },
    },
  })

  const { data: entryData, loading } = useQuery(SALARY_BASE_ENTRIES, {
    variables: {
      id: salaryBaseId,
      leaderGroups: form.leaderGroups ?? undefined,
      onlyNonExported: form.onlyNonExported,
    },
    fetchPolicy: 'cache-and-network',
  })

  return (
    <RegularModal
      title={translations.export}
      width="400px"
      submitDisabled={
        loading || (entryData?.allSalaryBaseEntries?.edges.length ?? 0) === 0
      }
      submitText={
        <div className="font-bold flex gap-2">
          {loading ? (
            <div className="inline">
              <Loader.Spinner colorPrimary="white" size={14} />
            </div>
          ) : (
            <Icon icon="download" size="14px" />
          )}
          {translations.exportNLines({
            n: entryData?.allSalaryBaseEntries?.edges.length ?? 0,
          })}
        </div>
      }
      onCancel={() => onSubmit(null)}
      onSubmit={submit(onSubmit)}
    >
      <Form>
        <Field>
          <label>{translations.exportFormat}</label>
          <Select
            value={form.exportFormat}
            options={options}
            fullWidth
            height="3.875rem"
            grayed
            onChange={value =>
              updateForm({
                exportFormat: value ?? ExportFormat.PDF,
              })
            }
          />
        </Field>
        <Field>
          <label>{translations.leaderGroups}</label>
          <LeaderGroupCombobox
            mode="multiple"
            value={form.leaderGroups}
            onChange={handler('leaderGroups')}
          />
        </Field>
        <Field>
          <label>{translations.onlyNonExported}</label>
          <Checkbox
            value={form.onlyNonExported}
            onChange={handler('onlyNonExported')}
          />
        </Field>
      </Form>
    </RegularModal>
  )
}
